import { type FieldValues, useController } from 'react-hook-form';

import { Dropdown, type DropdownProps } from '~/components/Dropdown';

import { FormField, type FormFieldProps } from '../../FormField';

type FormFieldDropdownProps<
  TValue extends string,
  TFormFields extends FieldValues,
  TMultiselect extends boolean | undefined,
> = Omit<FormFieldProps<TFormFields>, 'children'> &
  Omit<DropdownProps<TValue, TMultiselect>, 'name' | 'className'> & {
    onChange?: (value: string | string[]) => void;
  };

export function FormFieldDropdown<
  TValue extends string,
  TFormFields extends FieldValues,
  TMultiselect extends boolean | undefined,
>({
  name,
  label,
  className,
  disabled = false,
  readOnly = false,
  triggerClassName,
  labelAddon,
  ...dropdownProps
}: FormFieldDropdownProps<TValue, TFormFields, TMultiselect>) {
  const {
    field,
    fieldState: { invalid },
  } = useController({ name, disabled });

  return (
    <FormField
      name={name}
      label={label}
      className={className}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
    >
      <Dropdown
        {...field}
        state={invalid ? 'error' : undefined}
        {...dropdownProps}
        triggerClassName={triggerClassName}
        disabled={disabled || readOnly}
        onChange={(value) => {
          field.onChange(value);
          dropdownProps.onChange?.(value);
        }}
      />
    </FormField>
  );
}

FormFieldDropdown.displayName = 'FormFieldDropdown';
